import type { ElementType } from "react";

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { TEAMS } from "@/constants/appSidebar";

import { isStorybook, memoryStorage } from "./persistUtils.ts";

export type Team = {
  // 팀별 네비게이션(TEAM_NAV) 조회에 쓰는 안정적인 키.
  id: string;
  name: string;
  logo: ElementType;
  plan: string;
};

interface TeamState {
  // 현재 선택된 팀의 id. null 이면 사용하는 쪽에서 TEAMS[0] 을 기본값으로 사용한다.
  // logo 는 직렬화 불가능한 컴포넌트라 Team 전체가 아니라 이 id 만
  // "team-store" 라는 이름으로 localStorage 에 영속시킨다.
  activeTeamId: string | null;
  // 선택된 팀의 id 만 저장하여 영속시킨다.
  setActiveTeam: (team: Team) => void;
}

export const useTeamStore = create<TeamState>()(
  persist(
    (set) => ({
      activeTeamId: null,
      setActiveTeam: (team) => set({ activeTeamId: team.id }),
    }),
    {
      name: "team-store",
      // Storybook 에서 dev 앱의 영속 키를 덮어쓰지 않도록 in-memory 로 대체한다.
      storage: createJSONStorage(() =>
        isStorybook ? memoryStorage() : localStorage,
      ),
      // 직렬화 가능한 id 만 영속한다. 액션은 매 세션 초기화된다.
      partialize: (state) => ({ activeTeamId: state.activeTeamId }),
    },
  ),
);

// 영속된(오래됐거나 알 수 없거나 null 일 수 있는) id 를 실제 Team 으로 변환한다.
// 일치하는 팀이 없으면 첫 번째 팀을 기본값으로 사용한다.
export function resolveActiveTeam(activeTeamId: string | null): Team {
  return TEAMS.find((team) => team.id === activeTeamId) ?? TEAMS[0];
}

// 선택자 훅: 사용하는 쪽은 항상 null 이 아닌 완전한 Team 을 받는다.
export function useActiveTeam(): Team {
  const activeTeamId = useTeamStore((state) => state.activeTeamId);
  return resolveActiveTeam(activeTeamId);
}
